/**
 * #1058 — guard against an accidental re-dial right after a call ends.
 *
 * When the user taps "End call", the tablet flips to the idle screen,
 * which immediately renders the large green "Call" button. A stray
 * second touch (double-tap, touch-bounce, or the tablet being set
 * down) landing on that freshly-rendered button starts a brand-new
 * call within a few hundred ms of the hang-up — the companion greets
 * into silence, the call idle-times-out at 45 s with 0 user turns, and
 * it gets persisted as a phantom 0-turn conversation. Staging evidence
 * in the issue: 6 occurrences, every one immediately after a
 * user_hangup, all `call_type=normal` with no backing pending_call.
 *
 * The fix is a short cooldown: a user-initiated call started within
 * POST_HANGUP_COOLDOWN_MS of the previous call ending is treated as an
 * accidental tap and ignored. A deliberate call-back just needs one
 * more tap once the window passes.
 *
 * NB: this gates the user-initiated Call button ONLY. The proactive
 * call ANSWER path (a reminder / wellbeing / inactivity call arriving
 * right after a call) must still connect, so it deliberately bypasses
 * this guard.
 */

export const POST_HANGUP_COOLDOWN_MS = 1500;

/**
 * Return true if a new user-initiated call should be BLOCKED because a
 * previous call ended within the cooldown window.
 *
 * @param {number} lastCallEndedAt - epoch ms when the last call ended (0/falsy if none this session)
 * @param {number} now - current epoch ms
 * @param {number} [cooldownMs] - cooldown window in ms
 * @returns {boolean}
 */
export function isWithinReDialCooldown(
  lastCallEndedAt,
  now,
  cooldownMs = POST_HANGUP_COOLDOWN_MS,
) {
  if (!lastCallEndedAt) return false; // never ended a call this session
  const since = now - lastCallEndedAt;
  // Only the forward window blocks. A non-positive delta (clock skew)
  // or an elapsed window never blocks — fail open, never strand the
  // user from making a call.
  return since >= 0 && since < cooldownMs;
}
